'use client'

import { useEffect, useRef, useState } from 'react'

const DynamicTable = ({ children }: { children: React.ReactNode }) => {
  const tableRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [scrollPosition, setScrollPosition] = useState(0)

  useEffect(() => {
    const checkOverflow = () => {
      if (tableRef.current) {
        const container = tableRef.current
        const table = container.querySelector('table')
        if (table) {
          const containerWidth = container.clientWidth
          const tableWidth = table.scrollWidth
          setIsOverflowing(tableWidth > containerWidth)
        }
      }
    }

    const handleScroll = () => {
      if (tableRef.current) {
        setScrollPosition(tableRef.current.scrollLeft)
      }
    }

    checkOverflow()
    window.addEventListener('resize', checkOverflow)

    const tableElement = tableRef.current
    if (tableElement) {
      tableElement.addEventListener('scroll', handleScroll)
    }

    return () => {
      window.removeEventListener('resize', checkOverflow)
      if (tableElement) {
        tableElement.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  const getRoundedClasses = (): string => {
    // If table is not overflowing, apply full rounded corners
    if (!isOverflowing) {
      return 'rounded-2xl'
    }

    // Early return if tableRef is not available
    if (!tableRef.current) {
      return ''
    }

    const { scrollWidth, clientWidth } = tableRef.current

    // Calculate scroll thresholds with small tolerance for better UX
    const scrollThreshold = 2
    const isAtStart = scrollPosition <= scrollThreshold
    const isAtEnd = scrollPosition >= scrollWidth - clientWidth - scrollThreshold

    // Determine rounded corner classes based on scroll position
    if (isAtStart && isAtEnd) {
      // Table fits completely within container
      return 'rounded-2xl'
    } else if (isAtStart) {
      // Scrolled to the beginning
      return 'rounded-l-2xl'
    } else if (isAtEnd) {
      // Scrolled to the end
      return 'rounded-r-2xl'
    } else {
      // Scrolled to middle - no rounded corners
      return ''
    }
  }

  return (
    <div ref={tableRef} className={`overflow-auto border border-grey-200 shadow-lg ${getRoundedClasses()}`}>
      <table className="w-full min-w-[400px] overflow-scroll bg-white md:min-w-[800px]">
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

export default DynamicTable
